import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { AppointmentInfo } from '../../provider/sessions/appointment-info/AppointmentInfo';
import { getAppointmentById } from '../../../services/appointment/appointment.service';
import { Button, btnSize, btnType } from '../../../packages/ui/atoms/button';
import {
  badgeSize,
  badgeStyle,
  colors,
  fontWeight,
  IconButton,
  Icons,
  Text,
  textLevel,
} from '../../../packages';
import { WaitingRoomDetails } from '../sessions/waiting-room-details/WaitingRoomDetails';
import AppointmentScheduling from '../appointments/components/appointment-scheduling';
import { sxStyles, useStyles } from './UpcomingAppointment.styles';
import { ProfileInfo } from '../../../packages/ui/templates/profile-info';
import { Badge, badgeType } from '../../../packages';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { profileActionCreators } from '../../../redux/modules/profile/actions';
import { getAuth } from '../../../redux/modules/auth/selectors';

const UpcomingAppointment = () => {
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { meta } = useSelector(getAuth);
  const [loading, setLoading] = useState(false);
  const [appointment, setAppointment] = useState(null);
  const [showReschedule, setShowReschedule] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const classes = useStyles();

  const fetchAppointment = async () => {
    try {
      setLoading(true);
      const response = await getAppointmentById({ appointmentId });
      if (response?.status === 200) {
        setAppointment(response?.data);
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching appointment:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (appointmentId) {
      fetchAppointment();
    }
  }, [appointmentId]);

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleReschedule = () => {
    if (appointment) {
      setSelectedAppointment(appointment);
      setShowReschedule(true);
    }
  };

  const toggleRescheduleDrawer = () => {
    setShowReschedule(!showReschedule);
  };

  const handleStartSession = () => {
    navigate(`/provider/appointments/current/${appointmentId}/session`);
  };

  const handleAppointmentCancel = () => {
    if (meta?.userId) {
      const today = dayjs().format('YYYY-MM-DD');
      navigate(`/provider/${meta.userId}/day-schedule?date=${today}`);
    }
  };

  useEffect(() => {
    if (appointment?.patientId || appointment?.participantId) {
      const memberId = appointment.patientId || appointment.participantId;
      dispatch(profileActionCreators.fetchDemographicDetails(memberId));
      dispatch(profileActionCreators.fetchMemberInsurance({ memberId }));
    }
  }, [appointment]);

  return (
    <Box>
      <Box sx={sxStyles.header}>
        <Box display="flex" alignItems="center" gap="16px">
          <Button variant={btnType.OUTLINE} className={classes.btnBack} onClick={handleGoBack}>
            <Icons color={colors.primary500} glyph="arrow-left" width="20px" height="20px" />
          </Button>
          <Text level={textLevel.M} weight={fontWeight.SEMI_BOLD} color={colors.neutral900}>
            Upcoming appointment details
          </Text>
        </Box>
        <Box display="flex" gap="12px">
          <IconButton variant={btnType.OUTLINE} sx={sxStyles.outlineBtn} onClick={handleReschedule}>
            <Text level={textLevel.S} weight={fontWeight.BOLD} color={colors.primary600}>
              Reschedule
            </Text>
          </IconButton>
          <Button variant={btnType.PRIMARY} size={btnSize.SMALL} onClick={handleStartSession}>
            <Text level={textLevel.S} weight={fontWeight.BOLD} color={colors.white}>
              Start session
            </Text>
          </Button>
        </Box>
      </Box>

      <Box sx={sxStyles.body}>
        {loading || !appointment ? (
          <Box display="flex" alignItems="center" justifyContent="center" gap="6px" paddingY="58px">
            <Icons className="rotate linear infinite" glyph="in-progress" color={colors.primary} />
            <Text level={textLevel.M} weight={fontWeight.MEDIUM} color={colors.neutral900}>
              Loading...
            </Text>
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap="32px">
            <Box display="flex" flexDirection="column" gap="8px">
              <Box display="flex" alignItems="center" gap="12px">
                <Text level={textLevel.L} weight={fontWeight.SEMI_BOLD} color={colors.neutral900}>
                  {appointment.serviceName || 'Appointment'}
                </Text>
                <Badge variant={badgeType.FILLED} size={badgeSize.XSMALL} style={badgeStyle.PRIMARY_ACTIVE}>
                  <Text level={textLevel.XS} weight={fontWeight.SEMI_BOLD} color={colors.white}>
                    {appointment.status === 'BOOKED' ? 'Scheduled' : appointment.status}
                  </Text>
                </Badge>
              </Box>
              <Box display="flex" alignItems="center" gap="12px">
                <Text level={textLevel.M} weight={fontWeight.MEDIUM} color={colors.neutral700}>
                  {dayjs(appointment.startTime).format('MMMM D, YYYY')}
                </Text>
                <Box display="flex" alignItems="center" gap="12px">
                  <Box width="10px" height="10px" borderRadius="50%" backgroundColor={colors.neutral200} />
                  <Text level={textLevel.M} weight={fontWeight.MEDIUM} color={colors.neutral700}>
                    {dayjs(appointment.startTime).format('h:mm a')} -{' '}
                    {dayjs(appointment.endTime).format('h:mm a')}
                  </Text>
                </Box>
                <Box display="flex" alignItems="center" gap="12px">
                  <Box width="10px" height="10px" borderRadius="50%" backgroundColor={colors.neutral200} />
                  <Text level={textLevel.M} weight={fontWeight.MEDIUM} color={colors.neutral700}>
                    {appointment.firstAppointment ? '1st Appointment' : 'Follow up appointment'}
                  </Text>
                </Box>
              </Box>
            </Box>

            <Box className={classes.profileSection}>
              <ProfileInfo
                type="member"
                isProvider={true}
                memberId={appointment?.patientId || appointment?.participantId}
                photo={appointment?.participantImage || ''}
                profilePhotoName={
                  `${appointment?.patientFirstName || ''} ${appointment?.patientLastName || ''}`.trim() ||
                  appointment?.participantName ||
                  'Member'
                }
                nickName={
                  `${appointment?.patientFirstName || ''} ${appointment?.patientLastName || ''}`.trim() ||
                  appointment?.participantName ||
                  'Member'
                }
                fullName={`${appointment?.patientUuid} ${
                  appointment?.patientDOB
                    ? `• ${dayjs(appointment?.patientDOB, 'YYYY-MM-DD').format('MM/DD/YY')}`
                    : ''
                }`}
              />

              <ProfileInfo
                type="provider"
                isProvider={true}
                photo={appointment?.practitionerImage || ''}
                profilePhotoName={appointment?.practitionerName || 'Provider'}
                fullName={appointment?.practitionerName || 'Provider'}
                role={appointment?.practitionerDesignation || 'Provider'}
              />
            </Box>
          </Box>
        )}
        <Box display="flex" flexDirection="column" gap="16px">
          <AppointmentInfo appointment={appointment} isUpcomingAppointment={true} />
          <WaitingRoomDetails appointment={appointment} isUpcomingAppointment={true} />
        </Box>
      </Box>

      <AppointmentScheduling
        open={showReschedule}
        onClose={toggleRescheduleDrawer}
        appointment={selectedAppointment}
        updateAppointment={setSelectedAppointment}
        setIsApptCreated={isRefreshed => isRefreshed && void fetchAppointment()}
        onCancelAppointment={handleAppointmentCancel}
      />
    </Box>
  );
};

export default UpcomingAppointment;
